// src/components/AuditTrail.jsx
import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  User, 
  Download, 
  Eye, 
  CheckCircle, 
  AlertCircle, 
  Shield,
  Link as LinkIcon,
  FileText,
  Calendar,
  MapPin,
  Monitor,
  X
} from 'lucide-react';
import { apiService } from '../services/api';

export default function AuditTrail({ documentId, onClose }) {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAuditTrail();
  }, [documentId]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      const data = await apiService.request(`/documents/${documentId}/audit-trail`);
      setAuditData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'DOCUMENT_CREATED':
        return FileText;
      case 'LINK_GENERATED':
        return LinkIcon;
      case 'DOCUMENT_VIEWED':
        return Eye;
      case 'DOCUMENT_SIGNED':
        return CheckCircle;
      case 'DOCUMENT_VERIFIED':
        return Shield;
      case 'DOCUMENT_DOWNLOADED':
        return Download;
      case 'DOCUMENT_COMPLETED':
        return CheckCircle;
      case 'DOCUMENT_EXPIRED':
        return AlertCircle;
      default:
        return Clock;
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'DOCUMENT_CREATED':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'LINK_GENERATED':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'DOCUMENT_VIEWED':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'DOCUMENT_SIGNED':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'DOCUMENT_VERIFIED':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'DOCUMENT_COMPLETED':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'DOCUMENT_EXPIRED':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getActorBadge = (actorType) => {
    switch (actorType) {
      case 'creator':
        return { bg: 'bg-blue-500', label: 'Creador' };
      case 'signer':
        return { bg: 'bg-emerald-500', label: 'Firmante' };
      case 'verifier':
        return { bg: 'bg-indigo-500', label: 'Verificador' };
      case 'system':
        return { bg: 'bg-slate-500', label: 'Sistema' };
      default:
        return { bg: 'bg-slate-400', label: actorType };
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Cargando pista de auditoría...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 text-center mb-2">Error</h3>
          <p className="text-slate-600 text-center mb-4">{error}</p>
          <button
            onClick={onClose}
            className="w-full bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Pista de Auditoría</h2>
              <p className="text-slate-300 text-sm">
                {auditData?.totalEvents || 0} eventos registrados
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Stats */}
        {auditData?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-slate-50 border-b border-slate-200">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <Calendar className="w-4 h-4" />
                <span>Creado</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">
                {new Date(auditData.summary.createdAt).toLocaleDateString('es-ES')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <CheckCircle className="w-4 h-4" />
                <span>Firmado</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">
                {auditData.summary.signedAt 
                  ? new Date(auditData.summary.signedAt).toLocaleDateString('es-ES')
                  : 'Pendiente'
                }
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <User className="w-4 h-4" />
                <span>Firmantes</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">
                {auditData.summary.signedCount}/{auditData.summary.totalSigners}
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <Eye className="w-4 h-4" />
                <span>Verificaciones</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">
                {auditData.summary.totalVerifications}
              </p>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            {auditData?.events.map((event, index) => {
              const Icon = getEventTypeIcon(event.eventType);
              const colorClass = getEventTypeColor(event.eventType);
              const actorBadge = getActorBadge(event.actorType);

              return (
                <div key={index} className="flex gap-4">
                  {/* Timeline line */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {index < auditData.events.length - 1 && (
                      <div className="w-0.5 h-full bg-slate-200 mt-2"></div>
                    )}
                  </div>

                  {/* Event content */}
                  <div className="flex-1 pb-6">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-1">
                            {event.eventName}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${actorBadge.bg}`}>
                              {actorBadge.label}
                            </span>
                            <span className="text-xs text-slate-500">
                              {event.formattedTime}
                            </span>
                          </div>
                        </div>
                        
                        {event.actor && event.actorType !== 'system' && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900">
                              {event.actor}
                            </p>
                            {event.ipAddress && (
                              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                <MapPin className="w-3 h-3" />
                                <span className="font-mono">{event.ipAddress}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      {event.details && Object.keys(event.details).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            {Object.entries(event.details).map(([key, value]) => {
                              if (value === null || value === undefined) return null;
                              
                              let displayValue = value;
                              if (typeof value === 'boolean') {
                                displayValue = value ? 'Sí' : 'No';
                              } else if (typeof value === 'object') {
                                displayValue = JSON.stringify(value);
                              }

                              return (
                                <div key={key} className="flex gap-2">
                                  <dt className="font-medium text-slate-600 capitalize">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                                  </dt>
                                  <dd className="text-slate-900 font-mono break-all">
                                    {displayValue}
                                  </dd>
                                </div>
                              );
                            })}
                          </dl>
                        </div>
                      )}

                      {/* User Agent */}
                      {event.userAgent && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                          <Monitor className="w-3 h-3" />
                          <span className="truncate" title={event.userAgent}>
                            {event.userAgent.substring(0, 80)}...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 rounded-b-2xl flex justify-end">
          <button
            onClick={() => {
              // Exportar como JSON
              const dataStr = JSON.stringify(auditData, null, 2);
              const dataBlob = new Blob([dataStr], { type: 'application/json' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `audit-trail-${documentId}.json`;
              link.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Exportar JSON
          </button>
        </div>
      </div>
    </div>
  );
}